import { ArrowLeftOutlined, RightOutlined } from "@ant-design/icons"
import { Collapse, Flex, Tag, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import { SearchBar } from "../../components/Input/SearchBar.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { getAccessibleQuestionCategories } from "./content/guide-qa.ts"
import { useGuideTour } from "./tour/GuideTourContext.tsx"
import { useProductTour } from "./tour/ProductTourContext.tsx"

const { Title, Text } = Typography

/**
 * Questions & Answers — built around real operational questions, not a
 * static FAQ page: every category and every question is filtered through
 * the same permission checks as the rest of the Guide
 * (`getAccessibleQuestionCategories`), so a user never sees a question
 * about a feature they can't open. Clicking a question expands its answer
 * in place; "Show me in a guide" and "Open X" only render when the
 * question itself declares that seam, and both actions leave this page —
 * neither one is ever offered for something the question's own permission
 * gate wouldn't already allow.
 */
export const GuideQAPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { start: startProductTour } = useProductTour()
  const { activeTour: activeGuideTour, skip: skipGuideTour } = useGuideTour()
  const [query, setQuery] = useState("")
  const pages = useAppSelector((state) => state.auth?.permissions?.pages)
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)

  // The two tour engines don't guard against each other — starting the
  // Product Tour while a lesson tour is active would mount both overlays at
  // once (same fixed z-index, two sets of Next/Back/Skip controls).
  const handleShowMeInGuide = (stepId: string) => {
    if (activeGuideTour) skipGuideTour()
    startProductTour(stepId)
  }

  useEffect(() => {
    generateDocumentTitle(t("guide.qa.title"))
  }, [t])

  const groups = useMemo(
    () => getAccessibleQuestionCategories(pages, codes),
    [pages, codes]
  )

  const term = query.trim().toLowerCase()
  const filteredGroups = useMemo(() => {
    if (!term) return groups
    return groups
      .map((group) => ({
        category: group.category,
        questions: group.questions.filter((question) =>
          [
            t(question.questionKey),
            t(question.answerKey),
            ...(question.keywords ?? [])
          ]
            .join(" ")
            .toLowerCase()
            .includes(term)
        )
      }))
      .filter((group) => group.questions.length > 0)
  }, [groups, term, t])

  const totalResults = filteredGroups.reduce(
    (sum, group) => sum + group.questions.length,
    0
  )

  return (
    <Flex vertical gap={24} className="mx-auto max-w-[900px] pb-10">
      <Link
        to={RoutesPaths.guide.academy}
        className="flex items-center gap-1 text-sm text-gray-500"
      >
        <ArrowLeftOutlined /> {t("guide.academy.landing.title")}
      </Link>

      <Flex vertical gap={8}>
        <Title level={1} className="!mb-0">
          {t("guide.qa.title")}
        </Title>
        <Text className="text-xl" type="secondary">
          {t("guide.qa.subtitle")}
        </Text>
        <div className="mt-2 max-w-xl">
          <SearchBar
            size="large"
            placeholder={t("guide.qa.searchPlaceholder")}
            onSearch={(value) => setQuery(value ?? "")}
          />
        </div>
      </Flex>

      {term && (
        <Text type="secondary" className="text-sm">
          {t("guide.qa.resultCount", { count: totalResults })}
        </Text>
      )}

      {filteredGroups.length === 0 ? (
        <Text type="secondary">{t("guide.qa.noResults")}</Text>
      ) : (
        <Flex vertical gap={28}>
          {filteredGroups.map(({ category, questions }) => (
            <Flex vertical gap={12} key={category.id}>
              <Flex align="center" gap={8}>
                <Title level={4} className="!mb-0">
                  {t(category.titleKey)}
                </Title>
                <Tag>{questions.length}</Tag>
              </Flex>
              <Collapse
                bordered={false}
                items={questions.map((question) => ({
                  key: question.id,
                  label: <Text strong>{t(question.questionKey)}</Text>,
                  children: (
                    <Flex vertical gap={12}>
                      <Text className="text-sm leading-relaxed">
                        {t(question.answerKey)}
                      </Text>
                      <Flex gap={8} wrap>
                        {question.relatedGuideStepId && (
                          <Text
                            className="cursor-pointer text-sm font-medium"
                            style={{ color: "var(--ant-color-primary)" }}
                            onClick={() =>
                              handleShowMeInGuide(question.relatedGuideStepId!)
                            }
                          >
                            {t("guide.qa.showMeInGuide")}{" "}
                            <RightOutlined className="text-xs" />
                          </Text>
                        )}
                        {!question.relatedGuideStepId &&
                          question.relatedGuideId && (
                            <Link
                              to={RoutesPaths.guide.lesson(
                                question.relatedGuideId
                              )}
                              className="text-sm font-medium"
                              style={{ color: "var(--ant-color-primary)" }}
                            >
                              {t("guide.qa.showMeInGuide")}{" "}
                              <RightOutlined className="text-xs" />
                            </Link>
                          )}
                        {question.relatedRoute && (
                          <Text
                            className="cursor-pointer text-sm font-medium"
                            style={{ color: "var(--ant-color-primary)" }}
                            onClick={() => navigate(question.relatedRoute!)}
                          >
                            {t("guide.qa.openFeature", {
                              feature: t(category.titleKey)
                            })}{" "}
                            <RightOutlined className="text-xs" />
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                  )
                }))}
              />
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  )
}
